import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./AgendaScheduleHoverCard.css";
import { createPortal } from "react-dom";
import { LuCalendar, LuClock, LuMapPin, LuArrowRight, LuX } from "react-icons/lu";
import { getCategoryLabel } from "../../constants/agendaConstants";
import { getFormattedDateRange } from "../../utils/agendaUtils";

const CARD_WIDTH = 272;
const VIEWPORT_GUTTER = 12;
const CARD_GAP = 10;
const SIDE_GAP = 18;
const INDICATOR_WIDTH = 18;
const INDICATOR_EDGE_GUTTER = 18;

const clamp = (value, minimum, maximum) =>
  Math.min(Math.max(value, minimum), Math.max(minimum, maximum));

const getTimezone = (event) => {
  const eventTimezone = event?.timezone;

  if (eventTimezone) return eventTimezone;

  return Intl.DateTimeFormat().resolvedOptions().timeZone || "Local time";
};

export default function AgendaScheduleHoverCard({
  event,
  anchor,
  onClose,
  onPointerEnter,
  onPointerLeave,
  onViewDetails,
  placement = "vertical",
  sideAnchor,
}) {
  const cardRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!anchor || !anchor.isConnected) return undefined;

    const updatePosition = () => {
      const anchorRect = anchor.getBoundingClientRect();
      const cardWidth = cardRef.current?.offsetWidth || CARD_WIDTH;
      const cardHeight = cardRef.current?.offsetHeight || 280;
      const viewportRight = window.innerWidth - VIEWPORT_GUTTER;
      const viewportBottom = window.innerHeight - VIEWPORT_GUTTER;

      if (placement === "side") {
        const sideAnchorRect =
          sideAnchor?.isConnected ? sideAnchor.getBoundingClientRect() : anchorRect;
        const rightLeft = sideAnchorRect.right + SIDE_GAP + INDICATOR_WIDTH;
        const leftLeft =
          sideAnchorRect.left - SIDE_GAP - INDICATOR_WIDTH - cardWidth;
        const fitsRight = rightLeft + cardWidth <= viewportRight;
        const fitsLeft = leftLeft >= VIEWPORT_GUTTER;

        if (fitsRight || fitsLeft) {
          const side = fitsRight ? "right" : "left";
          const idealTop =
            anchorRect.top + anchorRect.height / 2 - cardHeight / 2;
          const top = clamp(
            idealTop,
            VIEWPORT_GUTTER,
            viewportBottom - cardHeight
          );
          const indicatorTop = clamp(
            anchorRect.top + anchorRect.height / 2 - top,
            INDICATOR_EDGE_GUTTER,
            cardHeight - INDICATOR_EDGE_GUTTER
          );

          setPosition({
            left: side === "right" ? rightLeft : leftLeft,
            top,
            side,
            indicatorTop,
          });
          return;
        }
      }

      const maxLeft = window.innerWidth - cardWidth - VIEWPORT_GUTTER;
      const left = clamp(anchorRect.left, VIEWPORT_GUTTER, maxLeft);
      const topBelow = anchorRect.bottom + CARD_GAP;
      const hasSpaceBelow = topBelow + cardHeight <= viewportBottom;

      setPosition({
        left,
        top: hasSpaceBelow
          ? topBelow
          : Math.max(VIEWPORT_GUTTER, anchorRect.top - cardHeight - CARD_GAP),
        side: null,
        indicatorTop: null,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchor, event, placement, sideAnchor]);

  useEffect(() => {
    if (anchor?.isConnected) return undefined;
    onClose();
    return undefined;
  }, [anchor, onClose]);

  if (!event || typeof document === "undefined") return null;

  return createPortal(
    <section
      ref={cardRef}
      className={`agenda-schedule-hover-card ${
        position?.side
          ? `agenda-schedule-hover-card-side-${position.side}`
          : ""
      }`}
      style={
        position
          ? {
              left: position.left,
              top: position.top,
              ...(position.side
                ? {
                    "--agenda-schedule-indicator-top": `${position.indicatorTop}px`,
                  }
                : {}),
            }
          : { visibility: "hidden" }
      }
      role="dialog"
      aria-label={`${event.title} schedule preview`}
      onMouseEnter={onPointerEnter}
      onMouseLeave={onPointerLeave}
      onFocus={onPointerEnter}
    >
      <div className="agenda-schedule-hover-header">
        <span
          className={`agenda-schedule-hover-category agenda-schedule-hover-category-${event.type}`}
        >
          {getCategoryLabel(event.type, true)}
        </span>
        <h4 className="agenda-schedule-hover-title">{event.title}</h4>
      </div>

      <button
        type="button"
        className="agenda-schedule-hover-close"
        onClick={onClose}
        aria-label="Close preview"
      >
        <LuX size={13} />
      </button>

      <div className="agenda-schedule-hover-details">
        {/* Date Row (Single or Range) */}
        <div className="agenda-schedule-hover-info">
          <span className={`agenda-schedule-hover-icon-chip agenda-schedule-hover-icon-chip-${event.type}`}>
            <LuCalendar aria-hidden="true" />
          </span>
          <span>{getFormattedDateRange(event.date, event.endDate)}</span>
        </div>

        {/* Time Row & Timezone */}
        <div className="agenda-schedule-hover-time-block">
          <div className="agenda-schedule-hover-info">
            <span className={`agenda-schedule-hover-icon-chip agenda-schedule-hover-icon-chip-${event.type}`}>
              <LuClock aria-hidden="true" />
            </span>
            <span>{event.time || event.startTime || "Time to be confirmed"}</span>
          </div>
          <span className="agenda-schedule-hover-timezone">
            {getTimezone(event)}
          </span>
        </div>

        {/* Location Row & City / Sublocation */}
        <div className="agenda-schedule-hover-location-block">
          <div className="agenda-schedule-hover-info">
            <span className={`agenda-schedule-hover-icon-chip agenda-schedule-hover-icon-chip-${event.type}`}>
              <LuMapPin aria-hidden="true" />
            </span>
            <span>
              {event.location || event.shortLocation || (event.isOnline ? "Online event" : "In-Person")}
            </span>
          </div>
          {event.shortLocation && event.location && event.shortLocation !== event.location && (
            <span className="agenda-schedule-hover-sublocation">
              {event.shortLocation}
            </span>
          )}
        </div>
      </div>

      <div className="agenda-schedule-hover-divider" />

      <div className="agenda-schedule-hover-about">
        <h5>About this {getCategoryLabel(event.type).toLowerCase()}</h5>
        <p>{event.description || "Details will be shared by the organizer."}</p>
      </div>

      <div className="agenda-schedule-hover-actions">
        <button
          type="button"
          className="agenda-schedule-hover-view-details"
          onClick={() => onViewDetails(event)}
        >
          View Details
          <LuArrowRight size={13} className="agenda-schedule-hover-view-arrow" />
        </button>
      </div>
    </section>,
    document.body
  );
}
